import React, { useCallback, useEffect, useState } from "react";
import { Redirect, Route, Switch } from "react-router-dom";

import { Button } from "@mui/material";
import { CompleteProfile, DealCreate, WelcomeModal } from "components/modals";
import {
  useCorrelationMatrixQueries,
  useDataroomsSocketHook,
  useDealsQueriesToUrl,
  useNavigate,
  useNotificationsSocketHook,
  usePrompt,
  useReportsQueries,
  useRoleGuardHook
} from "hooks";
import { Fallback, LoaderPage, NotFound } from "pages";
import {
  useAccountSelector,
  useHideCompletionPopupSelector,
  useHideWelcomePopupSelector,
  usePickUserPermissions,
  useUserActions,
  useWelcomeVideoUrlSelector
} from "store";
import { UserPermissionType } from "store/types";

import { RoleName } from "../../constants";
import { Routes } from "../../constants/routes";
import { MainRoutes } from "../Main-routers/MainRoutes";
import { useApiCalls } from "./use-api-calls";
import { useProtectedRoutesHooks } from "./use-protected-routes-hooks";

export const ProtectedRoutes: React.FC = (): JSX.Element => {
  useProtectedRoutesHooks();
  useNotificationsSocketHook();
  useDataroomsSocketHook();
  useDealsQueriesToUrl();
  useCorrelationMatrixQueries();

  const { getProfileCompletionAction, updateHideWelcomePopupAction } = useUserActions();
  const openPrompt = usePrompt();
  const navigate = useNavigate();
  const listenReports = useReportsQueries();
  const hideProfilePopup = useHideCompletionPopupSelector();
  const hideWelcomePopup = useHideWelcomePopupSelector();
  const welcomeVideoUrl = useWelcomeVideoUrlSelector();
  const account = useAccountSelector();
  const isSocialEnabled = account?.allow_social;
  const isDashboardEnabled = account?.allow_news;
  const freemiumMode = account?.is_freemium;
  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;

  const [showCompleteProfile, setShowCompleteProfile] = useState<boolean>(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(false);

  const isLoading = useApiCalls({ listenReports });

  const checkProfileCompletion = useCallback(async () => {
    if (!isSocialEnabled) return;
    const completePercentage = await getProfileCompletionAction();

    if (completePercentage === 100) return;

    setShowCompleteProfile(true);
  }, [isSocialEnabled]);

  const checkProfileCompletionWithRole = useRoleGuardHook(checkProfileCompletion, [RoleName.Admin, RoleName.Analyst]);

  const handleWelcomeClose = () => {
    setShowWelcomeModal(false);
  };

  useEffect(() => {
    if (isLoading) return;
    if (hideProfilePopup) return;

    const hasVideo = Boolean(welcomeVideoUrl);

    // Show Welcome Modal if not dismissed and video is configured
    if (!hideWelcomePopup && hasVideo) {
      setShowWelcomeModal(true);
      return; // Skip profile check, show Welcome first
    }

    // Show Complete Profile if no Welcome Modal
    checkProfileCompletionWithRole?.();
  }, [isLoading, hideProfilePopup, hideWelcomePopup, welcomeVideoUrl]);

  if (isLoading) {
    return <LoaderPage loading={isLoading} />;
  }

  const handleHomePageRoute = () => {
    if (freemiumMode) {
      return Routes.freemiumTools;
    }
    if (noAccessPermission) {
      return isDashboardEnabled ? Routes.dashboard : Routes.reports;
    }
    return isDashboardEnabled ? Routes.dashboard : Routes.myDeals;
  };

  const createDeal = async () => {
    const result = await openPrompt({ Component: DealCreate, props: {} });
    if (!result) return;
    navigate({ route: "deal", params: { deal_id: result.id } });
  };

  return (
    <>
      <Switch>
        <Route path={Routes.main} component={MainRoutes} />
        <Route path={Routes.notFound} component={NotFound} />
        <Route path={Routes.fallback} component={Fallback} />
        <Redirect exact from="/" to={handleHomePageRoute()} />
        <Redirect exact from="/login" to={handleHomePageRoute()} />
        <Redirect to={Routes.notFound} />
        {/* <Drawer open={!!DrawerContent}> */}
      </Switch>
      <WelcomeModal isOpen={showWelcomeModal} close={handleWelcomeClose} onGetStarted={updateHideWelcomePopupAction} />
      <CompleteProfile isOpen={showCompleteProfile} close={() => setShowCompleteProfile(false)} />
      <Button sx={{ visibility: "hidden", position: "absolute", top: 0 }} id="createDealModal" onClick={createDeal}>
        CreateDealModal Trigger
      </Button>
    </>
  );
};
